/**
 * 맥스매치 Full 상세.
 * GET /admin/max-match/full/{id} — 이미지·품목을 한 번에 조회하고,
 * 처방 수정은 PUT /source 전체 교체, 확정·실행은 Full 전용 API를 쓴다.
 */
import type { MaxMatchMiniMr } from '@/entities/max-match/api/max-match.api';
import {
  buildFullSourceReplaceRequest,
  type MaxMatchFullDetailLine,
} from '@/entities/max-match/api/max-match-full.api';
import {
  useConfirmMaxMatchFullBatch,
  useConfirmMaxMatchFullImage,
  useDownloadMaxMatchFullExcel,
  useMarkMaxMatchFullImageUnverifiable,
  useMaxMatchFullDetail,
  useReplaceMaxMatchFullSource,
  useRunMaxMatchFull,
} from '@/entities/max-match/hooks/useMaxMatchFull';
import { ocrService } from '@/entities/ocr/api/ocr.api';
import { preprocessService } from '@/entities/ocr/api/preprocess.api';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MonthPicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  OcrCropWarpUploader,
  type OcrCropWarpFileItem,
  type OcrCropWarpSelection,
} from '@/shared/components/OcrCropWarpUploader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { EditedOcrComparisonModal } from '@/pages/prescriptions/EditedOcrComparisonModal';
import {
  normalizeEditedOcrItems,
  type EditedOcrDrugRow,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import type { DrugMatchingResultItem } from '@/pages/prescriptions/prescriptionStagingUtils';
import {
  MaxMatchDrugInlineTable,
  type MaxMatchDrugEditDraft,
} from '@/pages/max-match/MaxMatchDrugInlineTable';
import {
  applyMiniDrugMatchingResults,
  getDuplicatedDrugGroups,
  getMrRowKey,
  mapEditedOcrRowsToMrs,
  mergeDrugsByCode,
  withMiniTempRowKey,
} from '@/pages/max-match/maxMatchMiniUtils';
import {
  canAdminConfirmImage,
  canMarkImageUnverifiable,
  imageReviewBadgeLabel,
  imageSelectUnverifiableClassName,
  isImageReviewed,
  isMaxMatchTerminalStatus,
  isUnverifiableStatus,
  reviewFinalizeToast,
} from '@/pages/max-match/maxMatchReviewStatus';
import {
  getBoundingBoxFromPoints,
  getInitialOcrCornerPoints,
} from '@/shared/utils/ocrCropWarpUtils';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';
import {
  buildOcrCoordinateStorageKey,
  loadOcrCoordinatesFromSession,
  OCR_COORDINATE_STORAGE_PREFIX,
  saveOcrCoordinatesToSession,
} from '@/shared/lib/ocrCoordinateStorage';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { mapMaxMatchLiteExcelRows } from '@/pages/max-match/maxMatchExcelMapper';
import {
  downloadXlsx,
  MAX_MATCH_LITE_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import {
  Check,
  CircleOff,
  Download,
  ExternalLink,
  Play,
  ScanLine,
  Sparkles,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FooterRow, ToolbarRow } from '@/shared/components/ui/row';
import {
  downloadUriFile,
  openFilePreview,
} from '@/shared/components/DownloadWithPreview';

type ConfirmKind =
  | 'deleteAll'
  | 'ocrOverwrite'
  | 'editedOcrOverwrite'
  | 'leave'
  | 'unverifiable'
  | null;

type FullMr = MaxMatchMiniMr & { pharmaCompanyId?: number | null };

const toMiniMr = (line: MaxMatchFullDetailLine): FullMr =>
  withMiniTempRowKey({
    mrId: line.sourceLineId,
    drugCode: line.drugCode,
    drugName: line.drugName,
    pharmaCompanyName: line.pharmaCompanyName,
    pharmaCompanyId: line.pharmaCompanyId,
    price: line.price,
    outHospitalQuantity: line.outHospitalQuantity,
    inHospitalQuantity: line.inHospitalQuantity,
    hasDrugInfo: line.hasDrugInfo,
    __tempRowKey: line.__tempRowKey,
  } as FullMr) as FullMr;

const fromMiniMr = (mr: FullMr, imageId: number): MaxMatchFullDetailLine => ({
  sourceLineId: mr.mrId,
  imageId,
  drugCode: mr.drugCode,
  drugName: mr.drugName,
  pharmaCompanyName: mr.pharmaCompanyName,
  pharmaCompanyId: mr.pharmaCompanyId,
  price: mr.price,
  outHospitalQuantity: mr.outHospitalQuantity,
  inHospitalQuantity: mr.inHospitalQuantity,
  hasDrugInfo: mr.hasDrugInfo,
  __tempRowKey: mr.__tempRowKey,
});

export const MaxMatchFullDetailPage: React.FC = () => {
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const navigate = useNavigate();
  const router = useRouter();
  const { maxMatchFullPrescriptionId: idParam } = useParams({
    strict: false,
  }) as { maxMatchFullPrescriptionId?: string };
  const prescriptionId = Number(idParam ?? 0);

  const {
    data: detail,
    isLoading: detailLoading,
    refetch,
  } = useMaxMatchFullDetail(prescriptionId);

  const [selectedImageId, setSelectedImageId] = useState(0);
  const [allLines, setAllLines] = useState<MaxMatchFullDetailLine[]>([]);
  const [drugData, setDrugData] = useState<FullMr[]>([]);
  const [prescriptionYm, setPrescriptionYm] = useState('');
  const [isYearMonthChanged, setIsYearMonthChanged] = useState(false);
  const [linesDirty, setLinesDirty] = useState(false);
  const [plainOcrPending, setPlainOcrPending] = useState(false);
  const [pendingEditedOcrSource, setPendingEditedOcrSource] = useState<
    'default' | 'gemini' | null
  >(null);

  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [ocrFileDialogOpen, setOcrFileDialogOpen] = useState(false);
  const [editedOcrImages, setEditedOcrImages] = useState<OcrCropWarpFileItem[]>(
    [],
  );
  const [ocrLiveSelection, setOcrLiveSelection] =
    useState<OcrCropWarpSelection | null>(null);
  const [ocrSourceInitialPoints, setOcrSourceInitialPoints] = useState<
    Point2D[]
  >([]);
  const [isPreparingOcrImageEditor, setIsPreparingOcrImageEditor] =
    useState(false);
  const [showDuplicateMergeModal, setShowDuplicateMergeModal] = useState(false);
  const [drugMatching, setDrugMatching] = useState(false);
  const [editedOcrComparing, setEditedOcrComparing] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<{
    default: EditedOcrDrugRow[];
    gemini: EditedOcrDrugRow[];
  }>({ default: [], gemini: [] });
  const [editedOcrRequestPreviewUrl, setEditedOcrRequestPreviewUrl] = useState<
    string | null
  >(null);
  const ocrCoordinatesFetchKeyRef = useRef<string | null>(null);

  const replaceSource = useReplaceMaxMatchFullSource();
  const confirmImage = useConfirmMaxMatchFullImage();
  const markUnverifiable = useMarkMaxMatchFullImageUnverifiable();
  const confirmBatch = useConfirmMaxMatchFullBatch();
  const runFull = useRunMaxMatchFull();
  const downloadExcel = useDownloadMaxMatchFullExcel();

  const images = detail?.images ?? [];
  const selectedImage = useMemo(
    () => images.find((img) => img.imageId === selectedImageId) ?? null,
    [images, selectedImageId],
  );

  const collectAllLines = (current = drugData): MaxMatchFullDetailLine[] => [
    ...allLines.filter((line) => line.imageId !== selectedImageId),
    ...current.map((mr) => fromMiniMr(mr, selectedImageId)),
  ];

  const handleSelectImage = (nextId: number) => {
    if (nextId === selectedImageId) return;
    const nextAll = collectAllLines();
    setAllLines(nextAll);
    setSelectedImageId(nextId);
    setDrugData(
      nextAll.filter((line) => line.imageId === nextId).map(toMiniMr),
    );
  };

  const persistSource = async (current = drugData) => {
    const payload = buildFullSourceReplaceRequest(
      prescriptionYm,
      collectAllLines(current),
    );
    if (payload.lines.length === 0) {
      toast.error('저장할 처방 품목이 없습니다.');
      return false;
    }
    await replaceSource.mutateAsync({
      maxMatchFullPrescriptionId: prescriptionId,
      payload,
    });
    setLinesDirty(false);
    setIsYearMonthChanged(false);
    await refetch();
    return true;
  };

  useEffect(() => {
    if (!detail || linesDirty) return;
    setAllLines(detail.lines);
    setPrescriptionYm(detail.sourceRefYm ?? '');
    const imageId = selectedImageId || detail.images[0]?.imageId || 0;
    if (imageId && imageId !== selectedImageId) {
      setSelectedImageId(imageId);
    }
    if (imageId) {
      setDrugData(
        detail.lines.filter((line) => line.imageId === imageId).map(toMiniMr),
      );
    }
  }, [detail, linesDirty, selectedImageId]);

  // 레거시 max-match-mini-ocr-coordinates sessionStorage 복원
  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    const stored = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (stored) {
      setOcrSourceInitialPoints(stored.points);
      ocrCoordinatesFetchKeyRef.current = sourceImageUrl ?? null;
    } else {
      setOcrSourceInitialPoints([]);
      ocrCoordinatesFetchKeyRef.current = null;
    }
  }, [selectedImage?.imageCode, selectedImage?.imageUrl]);

  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    if (
      !sourceImageUrl ||
      ocrSourceInitialPoints.length !== 4 ||
      !selectedImage?.imageCode
    ) {
      return;
    }
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    saveOcrCoordinatesToSession(storageKey, {
      sourceImageUrl,
      points: ocrSourceInitialPoints,
      bbox: getBoundingBoxFromPoints(ocrSourceInitialPoints),
    });
  }, [
    ocrSourceInitialPoints,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
  ]);

  useEffect(() => {
    const busy =
      isPreparingOcrImageEditor ||
      plainOcrPending ||
      editedOcrComparing ||
      drugMatching;
    setGlobalLoading(
      busy,
      isPreparingOcrImageEditor
        ? '편집 영역을 준비하는 중...'
        : drugMatching
          ? '의약품 매칭 요청 중...'
          : editedOcrComparing
            ? '편집 이미지 OCR 비교 요청 중...'
            : 'OCR 요청 중...',
    );
    return () => setGlobalLoading(false);
  }, [
    isPreparingOcrImageEditor,
    plainOcrPending,
    editedOcrComparing,
    drugMatching,
    setGlobalLoading,
  ]);

  const reviewedCount = useMemo(
    () => images.filter((img) => isImageReviewed(img.confirmStatus)).length,
    [images],
  );

  const confirmableCount = useMemo(
    () =>
      images.filter((img) => canAdminConfirmImage(img.confirmStatus)).length,
    [images],
  );

  const hasUnsavedDrugs = useMemo(
    () => drugData.some((item) => item.mrId == null),
    [drugData],
  );

  const duplicatedDrugGroups = useMemo(
    () => getDuplicatedDrugGroups(drugData),
    [drugData],
  );

  const isMatched =
    isMaxMatchTerminalStatus(detail?.status) || Boolean(detail?.latestRunId);

  const handleDeleteAllMrs = async () => {
    if (!selectedImageId) return;
    setDrugData([]);
    setLinesDirty(true);
    try {
      const remaining = collectAllLines([]);
      if (remaining.some((line) => line.drugCode)) {
        await persistSource([]);
      }
      toast.success('의약품이 삭제되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
    }
  };

  /** 레거시 runPlainOcr — Document AI process-url */
  const fetchPlainOcrMrs = async (): Promise<MaxMatchMiniMr[]> => {
    const raw = await ocrService.ocrPrescriptionFromUrl({
      imageUrl: selectedImage!.imageUrl!,
    });
    const rows = normalizeEditedOcrItems(raw);
    if (rows.length === 0) {
      throw new Error('OCR 결과가 없습니다.');
    }
    return mapEditedOcrRowsToMrs(rows);
  };

  const runOcr = async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('이미지가 선택되지 않았습니다.');
      return;
    }
    setPlainOcrPending(true);
    try {
      const drugList = await fetchPlainOcrMrs();
      setDrugData(drugList);
      setLinesDirty(true);
      toast.success('OCR 요청이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setPlainOcrPending(false);
    }
  };

  const fetchInitialOcrCoordinates = useCallback(async () => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const fetchKey = sourceImageUrl || null;
    if (!fetchKey) {
      setOcrSourceInitialPoints([]);
      return false;
    }

    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    const cached = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (cached) {
      ocrCoordinatesFetchKeyRef.current = fetchKey;
      setOcrSourceInitialPoints(cached.points);
      return true;
    }

    if (ocrCoordinatesFetchKeyRef.current === fetchKey) {
      return ocrSourceInitialPoints.length === 4;
    }

    setIsPreparingOcrImageEditor(true);
    try {
      const response = await preprocessService.getCornersFromUrl(
        sourceImageUrl!,
      );
      const nextPoints = getInitialOcrCornerPoints(response);
      if (nextPoints.length === 4) {
        ocrCoordinatesFetchKeyRef.current = fetchKey;
        setOcrSourceInitialPoints(nextPoints);
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: sourceImageUrl!,
          points: nextPoints,
          bbox: getBoundingBoxFromPoints(nextPoints),
        });
        return true;
      }
      setOcrSourceInitialPoints([]);
      return false;
    } catch (e) {
      console.error('Failed to prepare OCR coordinates:', e);
      toast.error(
        'OCR 좌표를 가져오지 못했습니다. 기본 영역으로 편집할 수 있습니다.',
      );
      setOcrSourceInitialPoints([]);
      return false;
    } finally {
      setIsPreparingOcrImageEditor(false);
    }
  }, [
    ocrSourceInitialPoints.length,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
    toast,
  ]);

  const handleOpenOcrImageEditor = useCallback(async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('편집할 원본 이미지가 없습니다.');
      return;
    }
    setEditedOcrImages([]);
    await fetchInitialOcrCoordinates();
    setOcrFileDialogOpen(true);
  }, [fetchInitialOcrCoordinates, selectedImage?.imageUrl, toast]);

  /** 레거시/처방검수: 닫을 때 편집 corners를 다음 오픈용 source로 재반영 */
  const closeOcrImageEditor = useCallback(() => {
    const meta = editedOcrImages[0]?.ocrMeta;
    const live = ocrLiveSelection;
    const corners = meta?.corners?.length === 4 ? meta.corners : live?.corners;
    const bbox =
      meta?.corners?.length === 4
        ? (meta.bbox ?? getBoundingBoxFromPoints(meta.corners))
        : live?.bbox;
    if (corners?.length === 4) {
      setOcrSourceInitialPoints(corners);
      if (selectedImage?.imageUrl) {
        const storageKey = buildOcrCoordinateStorageKey(
          selectedImage.imageCode || String(selectedImage.imageId),
          OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
        );
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: selectedImage.imageUrl,
          points: corners,
          bbox: bbox ?? getBoundingBoxFromPoints(corners),
        });
      }
    }
    setOcrFileDialogOpen(false);
    setEditedOcrImages([]);
    setOcrLiveSelection(null);
  }, [editedOcrImages, ocrLiveSelection, selectedImage]);

  const handleOcr = async () => {
    if (drugData.length > 0) {
      setConfirmKind('ocrOverwrite');
      return;
    }
    await runOcr();
  };

  const runEditedOcrComparison = async () => {
    const imageUrl = selectedImage?.imageUrl;
    const meta = editedOcrImages[0]?.ocrMeta;
    const corners = meta?.corners ?? ocrLiveSelection?.corners;
    const bbox = meta?.bbox ?? ocrLiveSelection?.bbox;
    const nextPreviewUrl =
      editedOcrImages[0]?.preview || ocrLiveSelection?.previewUrl || '';

    if (!imageUrl || !bbox || !Array.isArray(corners) || corners.length !== 4) {
      toast.error('OCR 좌표 영역을 먼저 선택해 주세요.');
      return;
    }

    setEditedOcrComparing(true);
    try {
      let previewForModal = nextPreviewUrl || null;
      if (nextPreviewUrl?.startsWith('blob:')) {
        try {
          const blob = await (await fetch(nextPreviewUrl)).blob();
          previewForModal = URL.createObjectURL(blob);
        } catch {
          previewForModal = null;
        }
      }

      const payload = { imageUrl, corners, bbox };
      const [defaultResult, geminiResult] = await Promise.allSettled([
        ocrService.ocrPrescriptionFromUrl(payload),
        ocrService.ocrPrescriptionFromUrlGemini(payload),
      ]);

      const normalizedDefault =
        defaultResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(defaultResult.value)
          : [];
      const normalizedGemini =
        geminiResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(geminiResult.value)
          : [];

      if (normalizedDefault.length === 0 && normalizedGemini.length === 0) {
        if (previewForModal?.startsWith('blob:')) {
          URL.revokeObjectURL(previewForModal);
        }
        throw new Error(
          'Document AI와 Gemini OCR 결과를 모두 가져오지 못했습니다.',
        );
      }

      setEditedOcrRequestPreviewUrl((prev) => {
        if (prev?.startsWith('blob:') && prev !== previewForModal) {
          URL.revokeObjectURL(prev);
        }
        return previewForModal;
      });
      setComparisonResults({
        default: normalizedDefault,
        gemini: normalizedGemini,
      });
      setComparisonOpen(true);
      setOcrFileDialogOpen(false);
      setEditedOcrImages([]);
      setOcrLiveSelection(null);

      if (
        defaultResult.status === 'rejected' ||
        geminiResult.status === 'rejected'
      ) {
        toast.error(
          '일부 OCR 요청이 실패했습니다. 조회된 결과만 비교 후 선택해 주세요.',
        );
      } else {
        toast.success('OCR 비교 결과를 불러왔습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setEditedOcrComparing(false);
    }
  };

  const applyEditedOcrRows = (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    setDrugData(mapEditedOcrRowsToMrs(selected));
    setLinesDirty(true);
    setComparisonOpen(false);
    setPendingEditedOcrSource(null);
    toast.success(
      `${source === 'gemini' ? 'Gemini' : 'Document AI'} OCR 결과를 반영했습니다.`,
    );
  };

  const applyEditedOcrResult = async (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    if (!selected.length) {
      toast.error('적용할 OCR 결과가 없습니다.');
      return;
    }

    // 레거시: 기존 약이 있으면 확인 후 삭제·적용
    if (drugData.length > 0 && selectedImageId) {
      setPendingEditedOcrSource(source);
      setConfirmKind('editedOcrOverwrite');
      return;
    }

    applyEditedOcrRows(source);
  };

  const submitDrugBatch = async (rows: FullMr[]) => {
    if (!selectedImageId) return;
    const saved = drugData.filter((item) => item.mrId != null);
    const next = [...saved, ...rows];
    try {
      const ok = await persistSource(next);
      if (ok) {
        setDrugData(next);
        toast.success('의약품이 등록되었습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleBatchAdd = async () => {
    if (!selectedImageId) return;
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    if (pendingDrugs.length === 0) {
      toast.error('일괄 등록할 의약품이 없습니다.');
      return;
    }
    if (pendingDrugs.some((item) => !item.drugCode)) {
      toast.error('의약품 코드가 비어있는 의약품이 있습니다.');
      return;
    }
    if (duplicatedDrugGroups.length > 0) {
      setShowDuplicateMergeModal(true);
      return;
    }
    await submitDrugBatch(pendingDrugs);
  };

  const handleConfirmDuplicateMerge = async () => {
    setShowDuplicateMergeModal(false);
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    await submitDrugBatch(mergeDrugsByCode(pendingDrugs));
  };

  const handleDrugMatching = async () => {
    if (drugData.length === 0) {
      toast.error('매칭할 의약품이 없습니다.');
      return;
    }
    if (!prescriptionYm) {
      toast.error('처방 년 월 정보가 없습니다.');
      return;
    }

    setDrugMatching(true);
    try {
      const items = drugData.map((item) => ({
        drugName: item.drugName ?? '',
        ...(item.drugCode ? { drugCode: item.drugCode } : {}),
        ...(item.pharmaCompanyName
          ? { pharmaCompanyName: item.pharmaCompanyName }
          : {}),
        ...(item.price != null ? { unitPrice: item.price } : {}),
        ...(item.inHospitalQuantity != null
          ? { inQuantity: item.inHospitalQuantity }
          : {}),
        ...(item.outHospitalQuantity != null
          ? { outQuantity: item.outHospitalQuantity }
          : {}),
      }));

      const response = await ocrService.searchDrugMatching({
        items,
        limit: 5,
        yearMonth: prescriptionYm,
      });

      if (response.metadata?.confidence === 'no_data') {
        toast.error(
          response.metadata.reason ||
            '해당 연월의 마스터 데이터가 없어 매칭할 수 없습니다.',
        );
        return;
      }

      const results = response.results as DrugMatchingResultItem[];
      if (results.length === 0) {
        toast.error('매칭 결과가 없습니다.');
        return;
      }

      const { nextList, matchedCount } = applyMiniDrugMatchingResults(
        drugData,
        results,
      );
      setDrugData(nextList);
      setLinesDirty(true);
      toast.success(`의약품 매칭 완료 (${matchedCount}/${drugData.length}건)`);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setDrugMatching(false);
    }
  };

  const handleConfirmImage = async () => {
    if (!selectedImageId) return;
    if (!canAdminConfirmImage(selectedImage?.confirmStatus)) {
      toast.error('이미 검수가 끝난 이미지입니다.');
      return;
    }
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    if (
      drugData.length > 0 &&
      !drugData.every((item) => item.hasDrugInfo === true)
    ) {
      toast.error('의약품 정보가 존재하지 않는 의약품이 있습니다.');
      return;
    }
    try {
      // 년월·라인 변경이 있으면 source 저장 후 확정
      if (isYearMonthChanged || linesDirty) {
        const hasLines = collectAllLines().some((line) =>
          Boolean(line.drugCode?.trim()),
        );
        if (hasLines) {
          const ok = await persistSource();
          if (!ok) return;
        } else {
          setLinesDirty(false);
          setIsYearMonthChanged(false);
        }
      }
      await confirmImage.mutateAsync({
        maxMatchFullPrescriptionId: prescriptionId,
        imageId: selectedImageId,
      });
      const { data: nextDetail } = await refetch();
      if (nextDetail?.status === 'UNVERIFIABLE') {
        toast.success(reviewFinalizeToast(nextDetail.status));
      } else {
        const nextImage = nextDetail?.images.find(
          (img) => img.imageId === selectedImageId,
        );
        toast.success(
          isUnverifiableStatus(nextImage?.confirmStatus)
            ? '확인불가로 처리되었습니다.'
            : reviewFinalizeToast(nextDetail?.status),
        );
      }
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleMarkUnverifiable = async () => {
    if (!selectedImageId) return;
    if (!canMarkImageUnverifiable(selectedImage?.confirmStatus)) {
      toast.error('이미 검수가 끝난 이미지입니다.');
      return;
    }
    try {
      await markUnverifiable.mutateAsync({
        maxMatchFullPrescriptionId: prescriptionId,
        imageId: selectedImageId,
      });
      const { data: nextDetail } = await refetch();
      toast.success(
        nextDetail?.status === 'UNVERIFIABLE'
          ? '내역 확인불가로 처리되었습니다.'
          : '확인불가로 처리되었습니다.',
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleConfirmAll = async () => {
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    const imageIds = images
      .filter((img) => canAdminConfirmImage(img.confirmStatus))
      .map((img) => img.imageId);
    if (imageIds.length === 0) {
      toast.error('확정할 이미지가 없습니다.');
      return;
    }
    try {
      if (isYearMonthChanged || linesDirty) {
        const hasLines = collectAllLines().some((line) =>
          Boolean(line.drugCode?.trim()),
        );
        if (hasLines) {
          const ok = await persistSource();
          if (!ok) return;
        } else {
          setLinesDirty(false);
          setIsYearMonthChanged(false);
        }
      }
      await confirmBatch.mutateAsync({
        maxMatchFullPrescriptionId: prescriptionId,
        imageIds,
      });
      const { data: nextDetail } = await refetch();
      toast.success(
        nextDetail?.status === 'UNVERIFIABLE'
          ? reviewFinalizeToast(nextDetail.status)
          : '전체 확정이 완료되었습니다.',
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleRunMaxMatch = async () => {
    try {
      const result = await runFull.mutateAsync(prescriptionId);
      await refetch();
      toast.success(
        result == null
          ? '확정 품목이 없어 실행하지 않았습니다.'
          : 'max match 생성이 완료되었습니다.',
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDownloadExcel = async () => {
    const runId = detail?.latestRunId;
    if (!runId) {
      toast.error('다운로드할 MaxMatch 실행 결과가 없습니다.');
      return;
    }
    try {
      const result = await downloadExcel.mutateAsync(runId);
      downloadXlsx(
        mapMaxMatchLiteExcelRows(result),
        `맥스매치_풀_${prescriptionId}`,
        MAX_MATCH_LITE_EXCEL_HEADER_MAP,
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleChangeDrugRows = (
    updater: (prev: MaxMatchMiniMr[]) => MaxMatchMiniMr[],
  ) => {
    setDrugData((prev) => updater(prev) as FullMr[]);
    setLinesDirty(true);
  };

  const applyDrugDraft = (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    const rowKey = getMrRowKey(row);
    handleChangeDrugRows((prev) =>
      prev.map((item) =>
        getMrRowKey(item) === rowKey ? { ...item, ...draft } : item,
      ),
    );
  };

  const handlePersistSavedRow = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    applyDrugDraft(row, draft);
  };

  const handlePersistStagingRow = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    applyDrugDraft(row, draft);
  };

  const handleDeleteSavedRow = async (row: MaxMatchMiniMr) => {
    const rowKey = getMrRowKey(row);
    handleChangeDrugRows((prev) =>
      prev.filter((item) => getMrRowKey(item) !== rowKey),
    );
  };

  const handlePersistLookup = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    applyDrugDraft(row, draft);
  };

  const isOcrBusy =
    plainOcrPending ||
    editedOcrComparing ||
    drugMatching ||
    replaceSource.isPending;

  if (detailLoading && !detail) {
    return <LoadingSpinner tip="맥스매치 Full 상세를 불러오는 중..." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="Maxmatch Full 처방 내역 확정"
        description={`처방 #${prescriptionId}`}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              className="min-w-[7.5rem]"
              onClick={() => {
                if (isYearMonthChanged || hasUnsavedDrugs || linesDirty) {
                  setConfirmKind('leave');
                  return;
                }
                if (window.history.length > 1) {
                  router.history.back();
                } else {
                  navigate({ to: ROUTE_PATHS.MAX_MATCH_FULL } as never);
                }
              }}
            >
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                size="sm"
                className="ruda-button min-w-[7.5rem]"
                disabled={
                  readOnly ||
                  images.length === 0 ||
                  confirmableCount === 0 ||
                  hasUnsavedDrugs ||
                  isMatched
                }
                onClick={handleConfirmAll}
              >
                전체 확정 완료
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <DataCard>
        <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label>사용자 이메일</Label>
            <Input value={detail?.email ?? ''} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>업체명</Label>
            <Input value={detail?.companyName ?? ''} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>등록일시</Label>
            <Input
              value={formatDateTime(detail?.createdAt)}
              disabled
              readOnly
            />
          </div>
          <div className="space-y-1">
            <Label>처방 년월</Label>
            <MonthPicker
              value={prescriptionYm}
              disabled={readOnly}
              onChange={(v) => {
                setPrescriptionYm(v);
                setIsYearMonthChanged(true);
              }}
              placeholder="년월 선택"
            />
          </div>
          <div className="space-y-1 sm:col-span-2 lg:col-span-2">
            <Label>max match 생성일시</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={formatDateTime(detail?.latestRunCreatedAt)}
                disabled
                readOnly
                className="min-w-0 flex-1"
              />
              <MenuWriteGate>
                <Button
                  size="sm"
                  className="ruda-button shrink-0"
                  disabled={
                    readOnly ||
                    images.length === 0 ||
                    reviewedCount !== images.length ||
                    isMatched
                  }
                  onClick={handleRunMaxMatch}
                >
                  <Play className="mr-2 h-4 w-4" />
                  max match 생성
                </Button>
              </MenuWriteGate>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                disabled={!detail?.latestRunId || downloadExcel.isPending}
                onClick={() => void handleDownloadExcel()}
              >
                <Download className="mr-2 h-4 w-4" />
                다운
              </Button>
            </div>
          </div>
        </CardContent>
      </DataCard>

      <FilterCard>
        <ToolbarRow justify="between">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-destructive">처방 이미지 선택</Label>
            <Select
              value={selectedImageId ? String(selectedImageId) : ''}
              onValueChange={(v) => handleSelectImage(Number(v))}
            >
              <SelectTrigger
                className={cn(
                  'w-72',
                  imageSelectUnverifiableClassName(
                    selectedImage?.confirmStatus,
                  ),
                )}
              >
                <SelectValue placeholder="처방 이미지 선택" />
              </SelectTrigger>
              <SelectContent>
                {images.map((img) => (
                  <SelectItem
                    key={img.imageId}
                    value={String(img.imageId)}
                    className={imageSelectUnverifiableClassName(
                      img.confirmStatus,
                    )}
                  >
                    {img.imageCode ?? `이미지 ${img.imageId}`} (
                    {imageReviewBadgeLabel(img.confirmStatus)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedImage?.imageUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openFilePreview(selectedImage.imageUrl!)}
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  보기
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void downloadUriFile(
                      selectedImage.imageUrl!,
                      selectedImage.imageCode || String(selectedImage.imageId),
                    ).catch((e) => handleApiError(e, toast));
                  }}
                >
                  <Download className="mr-1 h-3 w-3" />
                  다운로드
                </Button>
              </>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            검수/등록 이미지 수:{' '}
            <span className="font-medium text-foreground">
              {reviewedCount} / {images.length}
            </span>
          </div>
        </ToolbarRow>
      </FilterCard>

      {images.length === 0 ? (
        <EmptyState description="등록된 처방 이미지가 없습니다." />
      ) : (
        <>
          <MaxMatchDrugInlineTable
            rows={drugData}
            readOnly={readOnly}
            disabled={isMatched}
            refYm={prescriptionYm}
            onChangeRows={handleChangeDrugRows}
            onPersistSavedRow={handlePersistSavedRow}
            onPersistStagingRow={handlePersistStagingRow}
            onDeleteSavedRow={handleDeleteSavedRow}
            onPersistLookup={handlePersistLookup}
          />

          <ToolbarRow justify="between" gap={16}>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  className="ruda-button"
                  disabled={isMatched || isOcrBusy || readOnly}
                  onClick={handleOcr}
                >
                  <ScanLine className="mr-2 h-4 w-4" />
                  OCR
                </Button>
                <Button
                  variant="outline"
                  disabled={
                    isMatched ||
                    !selectedImage ||
                    isPreparingOcrImageEditor ||
                    readOnly
                  }
                  onClick={handleOpenOcrImageEditor}
                >
                  {isPreparingOcrImageEditor
                    ? '좌표 준비 중...'
                    : '이미지 편집 OCR'}
                </Button>
                <Button
                  className="ruda-button"
                  disabled={
                    isMatched || !hasUnsavedDrugs || drugMatching || readOnly
                  }
                  onClick={() => void handleDrugMatching()}
                >
                  의약품 매칭
                </Button>
                <Button
                  variant="outline"
                  disabled={isMatched || !hasUnsavedDrugs || readOnly}
                  onClick={() => void handleBatchAdd()}
                >
                  의약품 일괄 추가
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  variant="outline"
                  disabled={
                    readOnly ||
                    isMatched ||
                    markUnverifiable.isPending ||
                    !canMarkImageUnverifiable(selectedImage?.confirmStatus)
                  }
                  onClick={() => setConfirmKind('unverifiable')}
                >
                  <CircleOff className="mr-2 h-4 w-4" />
                  확인불가
                </Button>
                <Button
                  className="ruda-button"
                  disabled={
                    readOnly ||
                    hasUnsavedDrugs ||
                    isMatched ||
                    !canAdminConfirmImage(selectedImage?.confirmStatus)
                  }
                  onClick={() => void handleConfirmImage()}
                >
                  <Check className="mr-2 h-4 w-4" />
                  확정
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
          </ToolbarRow>
        </>
      )}

      <Dialog
        open={ocrFileDialogOpen}
        onOpenChange={(open) => !open && closeOcrImageEditor()}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>이미지 편집 OCR</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            선택된 처방 이미지를 편집한 뒤 OCR을 요청할 수 있습니다.
          </p>
          {isPreparingOcrImageEditor ? (
            <LoadingSpinner tip="편집 영역을 준비하는 중..." />
          ) : (
            <OcrCropWarpUploader
              maxCount={1}
              // 레거시 maxMatchMiniDetail: warp-crop + 해상도 제한 없음
              preprocessMode="warp-crop"
              maxSourceEdge={Number.POSITIVE_INFINITY}
              maxWarpWidth={Number.POSITIVE_INFINITY}
              maxWarpHeight={Number.POSITIVE_INFINITY}
              sourceImageUrl={selectedImage?.imageUrl}
              sourceImageName={`${selectedImage?.imageCode || 'prescription'}.png`}
              sourceButtonText="현재 처방 이미지 편집"
              allowFileSelect={false}
              sourceInitialPoints={ocrSourceInitialPoints}
              value={editedOcrImages}
              onChange={setEditedOcrImages}
              onSelectionChange={setOcrLiveSelection}
            />
          )}
          <FooterRow>
            <Button variant="outline" onClick={closeOcrImageEditor}>
              닫기
            </Button>
            <Button
              className="ruda-button"
              disabled={
                isPreparingOcrImageEditor ||
                editedOcrComparing ||
                !(
                  (editedOcrImages[0]?.ocrMeta?.corners?.length === 4 &&
                    editedOcrImages[0]?.ocrMeta?.bbox) ||
                  (ocrLiveSelection?.corners?.length === 4 &&
                    ocrLiveSelection?.bbox)
                )
              }
              onClick={() => void runEditedOcrComparison()}
            >
              OCR 요청 실행
            </Button>
          </FooterRow>
        </DialogContent>
      </Dialog>

      <EditedOcrComparisonModal
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        defaultRows={comparisonResults.default}
        geminiRows={comparisonResults.gemini}
        onApply={(source) => void applyEditedOcrResult(source)}
        applying={replaceSource.isPending}
        previewUrl={editedOcrRequestPreviewUrl}
      />

      <Dialog
        open={showDuplicateMergeModal}
        onOpenChange={setShowDuplicateMergeModal}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>동일 처방 의약품 확인</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            아래 의약품은 코드가 중복되어 있습니다. 사용량을 합산해 각 1건으로
            등록됩니다.
          </p>
          <div className="max-h-[420px] space-y-6 overflow-y-auto">
            {duplicatedDrugGroups.map((group) => (
              <div key={group.drugCode} className="space-y-2">
                <p className="font-medium">
                  {group.drugCode} · {group.drugName || '-'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {group.pharmaCompanyName || '-'} · 약가{' '}
                  {(group.price || 0).toLocaleString()}원
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>순번</TableHead>
                      <TableHead className="text-right">원외 사용량</TableHead>
                      <TableHead className="text-right">원내 사용량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((row, index) => (
                      <TableRow key={getMrRowKey(row)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="text-right">
                          {Number(
                            row.outHospitalQuantity ?? 0,
                          ).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {Number(row.inHospitalQuantity ?? 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">합계</TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalOutHospitalQuantity.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalInHospitalQuantity.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateMergeModal(false)}
            >
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleConfirmDuplicateMerge()}
            >
              합산하여 등록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmKind != null}
        onOpenChange={(open) => !open && setConfirmKind(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === 'unverifiable'
                ? '확인불가'
                : confirmKind === 'deleteAll' ||
                    confirmKind === 'ocrOverwrite' ||
                    confirmKind === 'editedOcrOverwrite'
                  ? '확인'
                  : '나가기'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === 'unverifiable'
                ? '이 이미지를 확인할 수 없는 처방으로 처리할까요? 원본은 보존되며 맥스매치 계산에서는 제외됩니다.'
                : confirmKind === 'ocrOverwrite' ||
                    confirmKind === 'editedOcrOverwrite'
                  ? '의약품이 이미 등록되어 있습니다. 삭제하고 다시 등록하시겠습니까?'
                  : confirmKind === 'leave'
                    ? '저장되지 않은 정보가 있습니다. 나가시면 저장되지 않습니다.'
                    : '선택한 항목을 삭제합니다.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirmKind === 'ocrOverwrite') {
                  await handleDeleteAllMrs();
                  await runOcr();
                  setConfirmKind(null);
                } else if (confirmKind === 'editedOcrOverwrite') {
                  if (!selectedImageId || !pendingEditedOcrSource) {
                    setConfirmKind(null);
                    return;
                  }
                  try {
                    setDrugData([]);
                    setLinesDirty(true);
                    applyEditedOcrRows(pendingEditedOcrSource);
                  } catch (e) {
                    handleApiError(e, toast);
                  } finally {
                    setConfirmKind(null);
                  }
                } else if (confirmKind === 'leave') {
                  if (window.history.length > 1) {
                    router.history.back();
                  } else {
                    navigate({ to: ROUTE_PATHS.MAX_MATCH_FULL } as never);
                  }
                  setConfirmKind(null);
                } else if (confirmKind === 'unverifiable') {
                  await handleMarkUnverifiable();
                  setConfirmKind(null);
                }
              }}
            >
              {confirmKind === 'leave'
                ? '나가기'
                : confirmKind === 'unverifiable'
                  ? '확인불가'
                  : '확인'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
